/**
 * Service result helpers
 *
 * ------------------------------------------------------------------
 * POR QUÉ EXISTE ESTE ARCHIVO (léelo antes de "simplificarlo")
 * ------------------------------------------------------------------
 *
 * Casi todos los servicios de `src/services/**` tienen esta forma:
 *
 *     try { ... } catch (error) {
 *       console.error('List X error:', error);
 *       return { data: null, count: 0, error };   // <-- DEVUELVE el error
 *     }
 *
 * Es decir: cuando la consulta falla, la promesa se RESUELVE. No rechaza.
 *
 * Todas las páginas de lista consumen eso con react-query:
 *
 *     const { data, error, refetch } = useQuery({ queryFn: () => listX(filters) });
 *
 * y más abajo tienen un bloque de error perfectamente escrito:
 *
 *     if (error) return <Alert>Error al cargar…<Button>Reintentar</Button></Alert>;
 *
 * Ese bloque es CÓDIGO MUERTO. El `error` de react-query solo se llena si la
 * queryFn rechaza; como la nuestra resuelve con `{ data: null, error }`,
 * react-query cree que todo salió bien y entrega `data.data === null`. La página
 * hace `const items = data?.data || []`, ve una lista vacía y renderiza el estado
 * vacío: "No se encontraron proyectos", "No hay tareas", "No hay materiales en el
 * inventario".
 *
 * O sea: ante una caída de red, un 500 de PostgREST o un rechazo de RLS, la
 * pantalla afirma con toda calma que NO HAY DATOS. Es la peor falla posible en un
 * sistema de registro — es indistinguible de "alguien borró todo" y no ofrece
 * ninguna acción, ni siquiera reintentar. Y como el `<Alert>` estaba escrito, en
 * una lectura del código el manejo de errores parece existir.
 *
 * `unwrapServiceResult` es el puente: convierte el error DEVUELTO en un error
 * LANZADO, que es lo único que react-query entiende. Se aplica en la queryFn, no
 * en el servicio, para no romper a los decenas de llamadores que ya viven con el
 * contrato `{data, error}`.
 *
 *     queryFn: () => unwrapServiceResult(listX(filters))
 *
 * @module utils/serviceResult
 */

/**
 * Espera un resultado de servicio con forma `{ data, error }` (o `{ success,
 * error }`) y lanza si trae error. Devuelve el resultado completo tal cual, para
 * que quien llama siga leyendo `.data` y `.count` como siempre.
 *
 * @template T
 * @param {Promise<T>|T} result - Resultado (o promesa) del servicio.
 * @returns {Promise<T>} El mismo resultado, si no hubo error.
 * @throws {Error} El error del servicio, normalizado a instancia de Error.
 */
export const unwrapServiceResult = async (result) => {
  const resolved = await result;

  if (resolved && resolved.error) {
    const { error } = resolved;
    throw error instanceof Error
      ? error
      : new Error(error?.message || 'Error inesperado al consultar los datos');
  }

  return resolved;
};

export default unwrapServiceResult;
